// components/DateTimeField.tsx
import React from 'react';
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import dayjs, { Dayjs } from 'dayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
type Props = {
  field: any;
  form: any;
  relatedFieldToClearError?: string;
};

export const DateTimeField: React.FC<Props> = ({
  field,
  form,
  relatedFieldToClearError,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState(false);
  const selectedDate = field.value ? dayjs(field.value) : dayjs();
  const error = form.touched[field.name] && form.errors[field.name];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (value: Dayjs | null) => {
    if (!value) return; // handle null case

    const updated = value
      .hour(selectedDate.hour())
      .minute(selectedDate.minute())
      .second(selectedDate.second());

    form.setFieldTouched(field.name, true);
    form.setFieldValue(field.name, updated.toISOString());

    if (relatedFieldToClearError) {
      maybeClearRelatedError(updated);
    }
  };

  const handleTimeChange = (newTime: Dayjs | null) => {
    if (!newTime) return;

    const updated = selectedDate
      .hour(newTime.hour())
      .minute(newTime.minute())
      .second(newTime.second());

    form.setFieldTouched(field.name, true);
    form.setFieldValue(field.name, updated.toISOString());

    if (relatedFieldToClearError) {
      maybeClearRelatedError(updated);
    }
  };
  // Function to clear related field error (e.g. clear bannerStartTime error when end time is valid)
  const maybeClearRelatedError = (updatedDate: Dayjs) => {
    if (!relatedFieldToClearError) return;

    const relatedValue = form.values[relatedFieldToClearError];
    if (!relatedValue) return;

    const relatedDate = dayjs(relatedValue);
    if (!relatedDate.isValid()) return;

    // Example logic: if current field is endTime and it's after startTime, clear startTime error
    if (field.name === 'bannerEndTime' && updatedDate.isAfter(relatedDate)) {
      form.setFieldError(relatedFieldToClearError, '');
    }

    // You can add more logic here for other cross-field clearing if needed
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ClickAwayListener onClickAway={handleClose}>
        <Box>
          <TextField
            value={selectedDate.format('YYYY-MM-DD hh:mm A')}
            onClick={handleClick}
            sx={{
              minWidth: 'unset',
              width: 400, // set width directly here
              '& .css-1ymeyxi-MuiInputBase-root-MuiOutlinedInput-root': {
                width: '400px',
              },
            }}
            inputProps={{
              readOnly: true,
              style: { cursor: 'pointer' },
              width: 400,
            }} // show pointer cursor on hover
            error={Boolean(error)}
            helperText={error ? form.errors[field.name] : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    // onClick={handleClick}
                    edge="end"
                    aria-label="open date picker"
                    size="small"
                  >
                    <CalendarTodayIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="top-start"
            sx={{ zIndex: 1300, width: '450px' }}
          >
            <Paper sx={{ display: 'flex', mt: 1 }}>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateChange}
                sx={{ width: '450px' }}
              />
              <MultiSectionDigitalClock
                sx={{
                  maxHeight: 'unset',
                  height: '336px',
                  padding: 0,
                  width: '220px',
                  '& .MuiMultiSectionDigitalClockSection-root': {
                    maxHeight: 'unset',
                    height: '25rem',
                    maxWidth: 'unset',
                    width: '3.125rem',
                    overflowY: 'scroll',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  },
                  '& .MuiMultiSectionDigitalClockSection-item': {
                    width: '2.5rem',
                    height: '2.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                  },
                }}
                ampm={true}
                timeSteps={{ hours: 1, minutes: 1 }}
                value={selectedDate}
                onChange={handleTimeChange}
                views={['hours', 'minutes']}
              />
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </LocalizationProvider>
  );
};
